import { useSelector, useDispatch } from 'react-redux';
import React, { useEffect, useState } from 'react';
import pluralize from 'library/helpers/pluralize';
import {
  useLocation,
  Switch,
  useRouteMatch,
  useParams,
  Route,
  Redirect,
} from 'react-router-dom';
import {
  getBrowseItems as getBrowseItemsSelector,
  getSelectedBrowseItems as getSelectedBrowseItemsSelector,
  getSelectedItemTypeIds as getSelectedItemTypeIdsSelector,
  getOpenedBrowseItem as getOpenedBrowseItemSelector,
  getBrowseItemsLoadingState as getBrowseItemsLoadingStateSelector,
  getBrowseItemsFilters as getBrowseItemsFiltersSelector,
  getItemById as getItemByIdSelector, getSelectedItems, getQueryArray,
} from 'store/browse-items/selectors';
import {
  getItemsLoading as getItemsLoadingSelector, getNextItem,
} from 'store/items/selectors';
import {
  selectItem as selectItemAction,
  deselectItem as deselectItemAction,
  deselectMany as deselectManyAction,
  resetBrowseItems, resetBrowseItemsActions,
  openBrowseItem as openBrowseItemAction,
  closeBrowseItem as closeBrowseItemAction,
  getBrowseItems as getBrowseItemsAction,
  setBrowseItemsFilters, getBrowseItemsDebaunced,
} from 'store/browse-items/actions';
import {
  batchUpdateItem,
  deleteItems, resetItem,
  resetNextItemData,
  setNextItemData,
} from 'store/items/actions';
import { BatchSelectContainer } from 'library/components/batch-select/styles';
import BatchSelect from 'library/components/batch-select';
import { TYPE as BROWSE_ENTITY_TYPE, FILTER_KEYS, TYPE } from 'store/browse-items/fixtures';
import Loader from 'library/components/entity-loader';
import Filters from 'library/components/filters';
import ViewMode from 'library/components/view-mode';
import ROUTES from 'library/constants/routes';
import withNavigationFilters from 'library/hocs/with-navigation-filters';
import CreateSetModal from 'library/components/modal/color';
import ExistingItemsModal from 'library/components/modal/pick-items-from-catelogue/catalogue';
import AddItemModal from 'library/components/modal/add-item-manually/index';
import {
  getSet,
  getSelectedSetItems,
} from 'store/set/selectors';
import * as yup from 'yup';
import { batchRemoveFromSet, createSet } from 'store/set/actions';
import NoContent from 'library/components/no-content';
import {
  getBarcodeItems as getBarcodeItemsAction,
  resetBarcodeItems, resetBarcodeItemsActions, searchBarcodeItem,
} from 'store/barcode-items/actions';
import SearchBarcodeModal from 'library/components/modal/input';
import { getViewMode } from '../../../store/shared/selectors';
import SetPage from './set';
import { getUpdatedValues, getBatchAddEntities } from '../../../library/components/item/helpers';
import PickItemsFromCatalogue from '../../../library/components/modal/pick-barcode-items';
import {
  getBarcodeItemSearchResult,
  getBarcodeItemSearchResultLoading,
} from '../../../store/barcode-items/selectors';

function capitalizeFirstLetter(string = '') {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

const {
  CATEGORIES,
  MANUFACTURERS,
  TAGS,
  QUANTITY,
  ITEMS,
  STORAGE,
  QUERY,
  COLORS,
} = FILTER_KEYS;

const validation = yup.object().shape({
  name: yup
    .string()
    .required('Set name is required field')
    .min(2, 'Name is too short')
    .max(30, 'Name is too long'),
  color: yup.string(),
});

const barcodeValidation = yup.object().shape({
  name: yup
    .string()
    .required('Barcode is required field')
    .min(6, 'Barcode is too short')
    .max(16, 'Barcode is too long'),
});

const Browse = () => {
  const dispatch = useDispatch();
  const items = useSelector(getBrowseItemsSelector);
  const searchBarcodeResult = useSelector(getBarcodeItemSearchResult);
  const filters = useSelector(getBrowseItemsFiltersSelector);
  const loading = useSelector(getBrowseItemsLoadingStateSelector);
  const isItemLoading = useSelector(getItemsLoadingSelector);
  const openedItemId = useSelector(getOpenedBrowseItemSelector);
  const selectedDataIds = useSelector(getSelectedBrowseItemsSelector);
  const selectedItemTypeIds = useSelector(getSelectedItemTypeIdsSelector);
  const selectedItems = useSelector(getSelectedItems);
  const selectedSetItems = useSelector(getSelectedSetItems);
  const { pathname } = useLocation();
  const showFilters = pathname !== ROUTES.SETTINGS && !pathname.includes('/set-details');
  const { path } = useRouteMatch();
  const mode = useSelector(getViewMode);
  const { id: setId } = useParams();
  const [isCreateSetOpened, setCreateSetOpened] = useState(false);
  const [searchBarcodeModalOpened, setSearchBarcodeModalOpened] = useState(false);
  const [isScanBarcodeModalOpened, toggleScanBarcodeModal] = useState(false);
  const [isExistingModalOpened, toggleExistingItemsModal] = useState(false);
  const [isManuallyModalOpened, toggleManuallyModal] = useState(false);
  const item = useSelector(getItemByIdSelector(openedItemId));
  const nextItem = useSelector(getNextItem);
  const set = useSelector(getSet);
  const baseQuery = useSelector(getQueryArray);

  useEffect(() => () => {
    dispatch(resetBrowseItems());
    dispatch(resetBrowseItemsActions());
  }, []);

  useEffect(() => {
    if(searchBarcodeResult && searchBarcodeModalOpened) {
      setSearchBarcodeModalOpened(false);
    }
  }, [searchBarcodeResult, searchBarcodeModalOpened]);

  useEffect(() => {
    if(!isManuallyModalOpened && nextItem && !isItemLoading) {
      setTimeout(() => {
        toggleManuallyModal(true);
      }, 1000);
    }
  }, [nextItem, isManuallyModalOpened, isItemLoading]);

  const handleToggleItemSelected = (id) => {
    if (!selectedDataIds.includes(id)) {
      return dispatch(selectItemAction({ id }));
    }

    return dispatch(deselectItemAction({ id }));
  };

  const handleOpenItem = (id) => {
    dispatch(resetBrowseItemsActions());

    dispatch(openBrowseItemAction({ id }));
  };

  const handleDeselectAll = (ids) => {
    dispatch(deselectManyAction({ ids }));
  };

  const handleEntitiesReset = () => {
    dispatch(resetBrowseItemsActions());
    dispatch(resetBrowseItems());
  };

  const handleLoadMore = () => {
    dispatch(getBrowseItemsAction());
  };

  const handleFilterChange = (values, isDebaunced = false) => {
    if(isDebaunced) {
      dispatch(getBrowseItemsDebaunced({
        values,
        filtersSnapshot: JSON.parse(JSON.stringify(filters)),
      }));
    } else {
      dispatch(setBrowseItemsFilters(values));
      dispatch(getBrowseItemsAction());
      dispatch(deselectManyAction({ ids: selectedItemTypeIds }));
    }
  };

  // const handleFilterReset = () => {
  //   dispatch(deselectManyAction({ ids: selectedItemTypeIds }));
  //
  //   dispatch(resetBrowseItems());
  //   dispatch(getBrowseItemsAction());
  // };

  const handleDeleteSetItems = (ids) => {
    const messages = {
      success: `You successfully removed ${pluralize('item', selectedDataIds.length)} from current set.`,
      error: 'Oops :( Remove failed. \nPlease try again.',
    };

    dispatch(batchRemoveFromSet({
      id: set.id,
      ids,
      messages,
      resetSet: true,
    }));

    dispatch(deselectManyAction({ ids }));
  };

  const handleDeleteItem = (ids) => {
    dispatch(deleteItems({ ids }));
    dispatch(deselectManyAction({ ids }));
  };

  const handleQueryParamDelete = ({ type, value }) => {
    const { [type]: updatedField, ...restFilters } = filters;

    dispatch(setBrowseItemsFilters({
      ...restFilters,
      ...(type === COLORS ? { [type]: updatedField.filter((filter) => filter.id !== value) } : {}),
      ...(type === MANUFACTURERS ? {
        [type]: updatedField.filter((filter) => {
          return (Array.isArray(value) ? !value.includes(filter) : filter !== value);
        }),
      } : {}),
      ...([TAGS, ITEMS].includes(type) ? { [type]: updatedField.filter((filter) => filter.id !== value) } : {}),
      ...([CATEGORIES, STORAGE].includes(type) ? { [type]: updatedField.filter((itemId) => itemId !== value) } : {}),
      ...(type === QUANTITY && { [type]: value || null }),
      ...(type === QUERY && { [type]: updatedField.split(',').filter((filter) => filter !== value).join(',') }),
    }));

    dispatch(deselectManyAction({ ids: selectedItemTypeIds }));
    dispatch(getBrowseItemsAction());
  };

  const handleQueryParamsReset = () => {
    dispatch(setBrowseItemsFilters({}));
    dispatch(getBrowseItemsAction());
    dispatch(closeBrowseItemAction());

    dispatch(deselectManyAction({ ids: selectedItemTypeIds }));
  };

  const setAddButtonActions = [
    // {
    //   id: 'manually',
    //   label: 'Add item manually',
    //   icon: 'icon-plus',
    //   action: () => {
    //     // toggleManuallyModal(true);
    //   },
    // },
  ];

  const addButtonActions = [
    ...(
      filters[FILTER_KEYS.TYPE] === TYPE.SET ? [
        {
          id: 'set',
          label: 'Create set',
          icon: 'icon-folder',
          action: () => {
            setCreateSetOpened(true);
          },
        },
        // {
        //   id: 'catalogue',
        //   label: 'Pick item from barcode catalogue',
        //   icon: 'icon-magnifier',
        //   action: () => {
        //     toggleScanBarcodeModal(true);
        //   },
        //   // disabled: true,
        // },
      ] : []
    ),
    ...(
      filters[FILTER_KEYS.TYPE] === TYPE.ITEM ? [
        {
          id: 'code',
          label: 'Search Barcode Catalog',
          icon: 'icon-magnifier',
          action: () => {
            dispatch(resetNextItemData());
            dispatch(resetBrowseItemsActions());
            dispatch(resetBarcodeItems());
            dispatch(getBarcodeItemsAction());

            toggleScanBarcodeModal(true);
          },
          // disabled: true,
        },
        {
          id: 'scan',
          label: 'Scan item barcode',
          icon: 'icon-barcode-scan',
          action: () => {
            dispatch(resetNextItemData());
            dispatch(resetBrowseItemsActions());
            dispatch(resetBarcodeItems());
            // dispatch(getBarcodeItemsAction());
            setSearchBarcodeModalOpened(true);
          },
        },
        {
          id: 'manually',
          label: 'Add item manually',
          icon: 'icon-plus',
          action: () => {
            dispatch(resetItem());
            dispatch(resetNextItemData());
            dispatch(resetBrowseItemsActions());
            dispatch(resetBarcodeItems());
            dispatch(getBarcodeItemsAction());
            toggleManuallyModal(true);
          },
        },
      ] : []
    ),
  ];

  const handleCreateSetModalClose = () => {
    setCreateSetOpened(false);
  };

  const handleExitingItemsModalClose = () => {
    toggleExistingItemsModal(false);
  };

  const handleScanBarcodeModalClose = () => {
    toggleScanBarcodeModal(false);
  };

  // eslint-disable-next-line no-unused-vars
  const handleManuallyModalClose = (isNewExpected) => {
    toggleManuallyModal(false);

    if(isNewExpected && nextItem?.is_barcode) {
      toggleScanBarcodeModal(true);
    }
  };

  const handleResetItem = () => {
    dispatch(resetItem());
  };

  const handleCloseItem = () => {
    dispatch(closeBrowseItemAction());
  };

  const onDeselect = (id) => {
    // dispatch(deleteWishlists({ ids }));
    dispatch(deselectManyAction({ ids: [id] }));
  };

  const handleAddToWishlistSubmit = () => {
    const data = { is_wishlisted: true };
    // todo - extend when reference data type is also updated
    const invalidIds = selectedItems.reduce((result, selectedItem) => {
      const isUpdateRedundant = Object.keys(data).some((key) => selectedItem[key] === data[key]);

      if (isUpdateRedundant) {
        return [...result, selectedItem.id];
      }

      return result;
    }, []);
    const validIds = selectedItemTypeIds.filter((id) => !invalidIds.includes(id));

    const messages = {
      success: `You successfully added ${pluralize('item', validIds.length)} to the wishlist.`,
      error: 'Oops :( Adding failed. \nPlease try again.',
      warning: `${pluralize('item', invalidIds.length)} ${invalidIds.length === 1 ? 'is' : 'are'} already present in the wishlist.`,
    };

    dispatch(batchUpdateItem({
      validIds,
      invalidIds,
      data: { is_wishlisted: true },
      messages,
    }));
  };

  const handleBarcodeCatalogueSubmit = ({ id, ...data }) => {
    toggleScanBarcodeModal(false);
    dispatch(setNextItemData({
      ...JSON.parse(JSON.stringify(data)),
      manufacturer: data.manufacturer ? [{ value: data.manufacturer }] : [],
      storage: data.storage ? [{ value: data.storage }] : [],
      is_barcode: true,
    }));

    setTimeout(() => {
      toggleManuallyModal(true);
    }, 1000);
  };

  const handleCreateSet = (data) => {
    dispatch(createSet({ data }));

    setCreateSetOpened(false);
  };

  const handleAddPropertiesBatchSubmit = () => (data, type) => {
    const NAMES_MAP = {
      tags: 'Tags',
      layers: 'Quantity',
      drawer: 'Storage location',
      manufacturer: 'Manufacturer',
    };

    const messages = {
      success: `${capitalizeFirstLetter(NAMES_MAP[type])} updated successfully.`,
      error: 'Oops :(\nWe could not apply this {value}.',
    };

    dispatch(batchUpdateItem({
      validIds: selectedItemTypeIds,
      data: getBatchAddEntities(getUpdatedValues(Object.keys(data).reduce((fields, key) => ({ ...fields, [key]: true }), {}), data)),
      messages,
      resetBrowseItems: true,
    }));
  };

  const handleSearchBarcodeModalClose = () => {
    setSearchBarcodeModalOpened(false);
  };

  return (
    <>
      <Filters
        hasAdditionalSortOptions
        hasEmptyManufacturers
        hasEmptyStorages
        filters={filters}
        baseQuery={baseQuery}
        isShown={showFilters}
        onChange={handleFilterChange}
        onDelete={handleQueryParamDelete}
        onReset={handleQueryParamsReset}
        areInactiveCategoriesIncluded
        multiselectProcessDelayMS={2000}
        isLoading={loading}
        isContentDisabledWhenOpen
      />
      <Switch>
        <Route path={`${path}/:mode/set-details/:id`} exact>
          <SetPage
            id={setId}
            openedItemId={openedItemId}
          >
            <ViewMode
              data={set?.items || []}
              type={BROWSE_ENTITY_TYPE.ITEM}
              openedItemId={openedItemId}
              handleOpen={handleOpenItem}
              handleToggle={handleToggleItemSelected}
              handleClose={handleCloseItem}
              selectedIds={selectedDataIds}
              addButtonActions={setAddButtonActions}
              openedItemData={item}
            />
            <BatchSelectContainer>
              <BatchSelect
                type={filters[FILTER_KEYS.TYPE]}
                data={selectedSetItems}
                onDeselectAll={() => handleDeselectAll(selectedDataIds)}
                onDeselect={onDeselect}
                onAddToWishlist={handleAddToWishlistSubmit}
                onBatchAddProperties={handleAddPropertiesBatchSubmit()}
                onDelete={() => handleDeleteSetItems(selectedDataIds)}
                deleteType="set-item"
                disabledActions={['heart']}
              />
            </BatchSelectContainer>
          </SetPage>
        </Route>
        <Route path={`${path}/:mode`} exact>
          <ViewMode
            data={items}
            type={filters[FILTER_KEYS.TYPE]}
            openedItemId={openedItemId}
            handleOpen={handleOpenItem}
            handleToggle={filters[FILTER_KEYS.TYPE] !== TYPE.SET ? handleToggleItemSelected : undefined}
            handleClose={handleCloseItem}
            handleReset={handleResetItem}
            selectedIds={filters[FILTER_KEYS.TYPE] === TYPE.SET ? [] : selectedDataIds}
            addButtonActions={addButtonActions}
          />
          {items.length === 0 && !loading ? (
            <NoContent
              title={filters[FILTER_KEYS.TYPE] === TYPE.SET ? 'There are no sets to display' : 'No matching items'}
            />
          ) : null}
          <Loader
            isStopped={!!openedItemId}
            isLoading={loading || isItemLoading}
            currentAmount={items.length}
            hasMore={filters[FILTER_KEYS.HAS_MORE]}
            onScroll={handleLoadMore}
            onReset={handleEntitiesReset}
          />
          <BatchSelectContainer>
            <BatchSelect
              type={filters[FILTER_KEYS.TYPE]}
              data={selectedItems}
              deleteType={filters[FILTER_KEYS.TYPE] === TYPE.SET ? 'set' : 'item'}
              onDeselectAll={() => handleDeselectAll(selectedDataIds)}
              onDeselect={onDeselect}
              onAddToWishlist={handleAddToWishlistSubmit}
              onBatchAddProperties={handleAddPropertiesBatchSubmit()}
              onDelete={() => handleDeleteItem(selectedDataIds)}
              disabledActions={[]}
              addConfig={{
                resetBrowseItems: true,
                resetWishlistItems: false,
              }}
            />
          </BatchSelectContainer>
        </Route>
        <Redirect to={`${ROUTES.BROWSE}/${mode}`} />
      </Switch>
      <CreateSetModal
        title="Create new set"
        iconClassName="icon-pencil"
        submitText="Create new set"
        cancelText="Cancel"
        isOpened={isCreateSetOpened}
        onClose={handleCreateSetModalClose}
        onSubmit={handleCreateSet}
        overlayClassName="Overlay"
        placeholder="Come up with a name for your set"
        validation={validation}
      />
      <ExistingItemsModal
        itemText="Ergonomic Soft Chips"
        title="Browse existing items"
        isOpened={isExistingModalOpened}
        onClose={handleExitingItemsModalClose}
        validation={validation}
        overlayClassName="Overlay"
        placeholder="Find an item"
        width="1018px"
        footer={false}
      />
      <PickItemsFromCatalogue
        width="1020px"
        title="Search Barcode Catalog"
        // submitText="Save"
        isOpened={isScanBarcodeModalOpened}
        onClose={handleScanBarcodeModalClose}
        onSubmit={handleBarcodeCatalogueSubmit}
      />
      <SearchBarcodeModal
        title="Enter barcode"
        iconClassName="icon-pencil"
        submitText="Search catalog using barcode"
        cancelText="Cancel"
        isOpened={searchBarcodeModalOpened}
        onClose={handleSearchBarcodeModalClose}
        onSubmit={(value) => {
          dispatch(searchBarcodeItem({ barcode: value.name.trim() }));
        }}
        overlayClassName="Overlay"
        placeholder="Enter barcode"
        validation={barcodeValidation}
        isFocusedOnMount
      />
      {
        searchBarcodeResult ? (
          <AddItemModal
            isAddMoreEnabled
            isOpened={searchBarcodeResult && !searchBarcodeModalOpened}
            cancelText="Back to scan"
            onCreateMore={() => {
              dispatch(resetBarcodeItemsActions());
              setTimeout(() => {
                setSearchBarcodeModalOpened(true);
              }, 1000);
            }}
            onCancel={
              () => {
                setTimeout(() => {
                  setSearchBarcodeModalOpened(true);
                }, 500);
              }
            }
            defaultData={{
              name: searchBarcodeResult?.description ?? '',
              category: searchBarcodeResult?.category_id ?? '',
              images: searchBarcodeResult?.images || [],
              storage: searchBarcodeResult?.storage_id ? [{ value: searchBarcodeResult?.storage_id }] : [],
              barcode: searchBarcodeResult?.upc?.trim() || searchBarcodeResult?.ean?.trim() || '',
              price: searchBarcodeResult?.barcode_params?.find((param) => param?.name === 'retail_price')?.value ? parseFloat(searchBarcodeResult?.barcode_params.find((param) => param?.name === 'retail_price')?.value)?.toFixed(2) : '',
              note: searchBarcodeResult?.note || '',
              manufacturer: searchBarcodeResult?.manufacturer_id ? [{ text: searchBarcodeResult?.manufacturer_name, value: searchBarcodeResult?.manufacturer_id }] : [],
              is_barcode: true,
            }}
            labelMaps={{
              ...(searchBarcodeResult?.category ? {
                categories: {
                  [searchBarcodeResult.category]: searchBarcodeResult.category_name,
                },
              } : {}),
              ...(searchBarcodeResult?.manufacturer_id ? {
                manufacturers: {
                  [searchBarcodeResult.manufacturer_id]: searchBarcodeResult.manufacturer_name,
                },
              } : {}),
            }}
            onClose={() => {
              dispatch(resetBarcodeItemsActions());
              setSearchBarcodeModalOpened(false);
            }}
            overlayClassName="Overlay"
            placeholder="Name this item (required)"
            name="name"
            saveText="Add item"
            saveAndCreateAnotherButtonText="Add and create another"
          />
        ) : (
          <AddItemModal
            isAddMoreEnabled
            saveText="Add Item"
            saveAndCreateAnotherButtonText="Add and create another"
            isOpened={isManuallyModalOpened}
            onCreateMore={nextItem?.is_barcode ? () => {
              dispatch(resetNextItemData());

              setTimeout(() => {
                toggleScanBarcodeModal(true);
              }, 1000);
            } : undefined}
            defaultData={nextItem || {
              manufacturer: [],
              storage: [],
              tags: [],
            }}
            labelMaps={{
              ...(nextItem?.category ? {
                categories: {
                  [nextItem.category]: nextItem.category_name,
                },
              } : {}),
              ...(nextItem?.manufacturer?.[0]?.value ? {
                manufacturers: {
                  [nextItem.manufacturer?.[0]?.value]: nextItem?.manufacturer_name,
                },
              } : {}),
            }}
            onClose={handleManuallyModalClose}
            overlayClassName="Overlay"
            placeholder="Name this item (required)"
            name="name"
          />
        )
      }
    </>
  );
};

export default withNavigationFilters(Browse);
